import { Box, Flex, Heading } from '@chakra-ui/react';
import _ from 'lodash';
import React from 'react';
import { useHistory } from 'react-router-dom';

import { PortfolioCoreBorrowingBaseChartTemplate } from '../shared/PortfolioCoreBorrowingBaseChartTemplate';
import { PortfolioVisibilityGroupButton } from '../shared/PortfolioVisibilityGroupButton';
import { PortfolioStatementsContainer } from '../statements/PortfolioStatementsContainer';
import {
  chargeOffAndDelinquentOverviewChartConfig,
  collectionsAccountChartConfig,
  daysPastDueChartConfig,
} from './profitLossChartConfigs';
import { ProfitLossOverviewChartTemplate } from './ProfitLossOverviewChartTemplate';
import { ProfitLossVintageChartTemplate } from './ProfitLossVintageChartTemplate';
import { RepaymentsVintageChart } from './RepaymentsVintageChart';
import { colors } from 'packages/common-frontend/colors';
import { HighlightBox } from 'packages/common-frontend/components/HighlightBox';
import { useQueryParams } from 'packages/common-frontend/hooks/useQueryParams';
import { creditFacilityIdQueryParam } from 'packages/credit-facility/ui/CreditFacilitySelect';

enum ProfitLossDisplayModeKeys {
  all = 'all',
  overview = 'overview',
  vintage = 'vintage',
  statements = 'statements',
}
const isProfitLossDisplayMode = (
  test: any,
): test is ProfitLossDisplayModeKeys => {
  return (
    test &&
    typeof test === 'string' &&
    _.includes(ProfitLossDisplayModeKeys, test)
  );
};

const profitLossDisplayModeQueryParam = 'display';

type ProfitLossButtonGroupDisplay = {
  key: ProfitLossDisplayModeKeys;
  label: string;
};
const profitLossButtonGroupOptions: Record<
  ProfitLossDisplayModeKeys,
  ProfitLossButtonGroupDisplay
> = {
  all: {
    key: ProfitLossDisplayModeKeys.all,
    label: 'Show All',
  },
  overview: {
    key: ProfitLossDisplayModeKeys.overview,
    label: 'Overview',
  },
  vintage: {
    key: ProfitLossDisplayModeKeys.vintage,
    label: 'By Vintage',
  },
  statements: {
    key: ProfitLossDisplayModeKeys.statements,
    label: 'Statements',
  },
};

export const ProfitLossContainer: React.FC<{
  creditFacilityId: string;
}> = ({ creditFacilityId }) => {
  const history = useHistory();
  const query = useQueryParams();
  const queryDisplayMode = query.get(profitLossDisplayModeQueryParam);
  const displayMode = isProfitLossDisplayMode(queryDisplayMode)
    ? queryDisplayMode
    : 'all';

  const onSetDisplayMode = (selectedDisplayMode: ProfitLossDisplayModeKeys) => {
    const covenantsBaseRoute = '/portfolio/profit-loss';
    const queryParams = `?${creditFacilityIdQueryParam}=${creditFacilityId}&${profitLossDisplayModeQueryParam}=${selectedDisplayMode}`;

    history.push(`${covenantsBaseRoute}${queryParams}`);
  };
  return (
    <Box>
      <Flex mb="32px" style={{ gap: '16px' }}>
        {_.map(profitLossButtonGroupOptions, ({ key, label }) => (
          <PortfolioVisibilityGroupButton
            selected={displayMode === key}
            label={label}
            onClick={() => onSetDisplayMode(key)}
            key={`profit-loss-visibility-group-button-${key}`}
          />
        ))}
      </Flex>
      {(displayMode === ProfitLossDisplayModeKeys.all ||
        displayMode === ProfitLossDisplayModeKeys.overview) && (
        <>
          <ProfitLossOverviewChartTemplate
            creditFacilityId={creditFacilityId}
            chartTitle="Charge Off & Delinquent"
            chartConfig={chargeOffAndDelinquentOverviewChartConfig}
          />
          <ProfitLossOverviewChartTemplate
            creditFacilityId={creditFacilityId}
            chartTitle="Days Past Due"
            chartConfig={daysPastDueChartConfig}
          />
          <PortfolioCoreBorrowingBaseChartTemplate
            creditFacilityId={creditFacilityId}
            chartTitle="Collections Account"
            chartConfig={collectionsAccountChartConfig}
          />
        </>
      )}
      {(displayMode === ProfitLossDisplayModeKeys.all ||
        displayMode === ProfitLossDisplayModeKeys.vintage) && (
        <>
          <Heading as="h3" fontSize="24px" mb="24px">
            By Vintage
          </Heading>
          <HighlightBox backgroundColor={colors.millet[50]}>
            <RepaymentsVintageChart creditFacilityId={creditFacilityId} />
            <ProfitLossVintageChartTemplate
              creditFacilityId={creditFacilityId}
              chartTitle="Charge Off By Vintage"
              field="chargedOffPercentageOfTotalExposure"
              fieldType="percentage"
              yAxisLabel="Percent of Total Exposure"
            />
            <ProfitLossVintageChartTemplate
              creditFacilityId={creditFacilityId}
              chartTitle="Delinquency By Vintage"
              field="delinquentPercentageOfTotalExposure"
              fieldType="percentage"
              yAxisLabel="Percent of Total Exposure"
            />
          </HighlightBox>
        </>
      )}
      {(displayMode === ProfitLossDisplayModeKeys.all ||
        displayMode === ProfitLossDisplayModeKeys.statements) && (
        <PortfolioStatementsContainer creditFacilityId={creditFacilityId} />
      )}
    </Box>
  );
};
